import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { ShieldCheck, Loader2, Trash2, Eye, EyeOff } from 'lucide-react';
import { manageGet, managePatch, manageDelete, manageGetIntros, manageRespondIntro } from '../services/poolApi';

/* ----------------------------------------------------------------------------
 * Manage profile / visibility — account-less candidate self-service. Reached via
 * /profile/manage?token=<manage_token>. The token is the credential: load the
 * candidate's own profile, let them toggle discoverability, set availability,
 * edit roles/skills, or remove themselves from the pool entirely. Blue brand,
 * matches Join.js / Register.js.
 * -------------------------------------------------------------------------- */

const BLUE = '#2563EB';

const AVAILABILITY = [
  { value: 'open_now', label: 'Open now — actively looking' },
  { value: 'open_casual', label: 'Casually open' },
  { value: 'not_looking', label: 'Not looking right now' },
];

const TIMELINE = [
  { value: 'asap', label: 'ASAP' },
  { value: '1-3mo', label: '1–3 months' },
  { value: '3-6mo', label: '3–6 months' },
  { value: 'exploring', label: 'Just exploring' },
];

function ManageProfile() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  // Editable local copy of the comma-joined skills + desired roles strings.
  const [desiredRoles, setDesiredRoles] = useState('');
  const [skills, setSkills] = useState('');
  const [mustHaves, setMustHaves] = useState('');
  const [dealBreakers, setDealBreakers] = useState('');
  const [intros, setIntros] = useState([]);

  const joinArr = (a) => (Array.isArray(a) ? a.join(', ') : '');
  const splitArr = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

  const loadIntros = async (t) => {
    try {
      const res = await manageGetIntros(t);
      setIntros(res.data.intros || []);
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError('No token provided. Use the link from your signup confirmation.');
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const res = await manageGet(token);
        if (cancelled) return;
        setProfile(res.data);
        setDesiredRoles(res.data.desired_roles || '');
        setSkills(joinArr(res.data.skills));
        setMustHaves(joinArr(res.data.must_haves));
        setDealBreakers(joinArr(res.data.deal_breakers));
        loadIntros(token);
      } catch (err) {
        if (cancelled) return;
        setError(
          err.response?.status === 404
            ? "We couldn't find a profile for this link. It may have been removed."
            : err.response?.data?.error || 'Failed to load your profile.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Persist a partial patch and merge the returned self-view into local state.
  const patch = async (data) => {
    setSaving(true);
    try {
      const res = await managePatch(token, data);
      setProfile(res.data);
      setDesiredRoles(res.data.desired_roles || '');
      setSkills(joinArr(res.data.skills));
      setMustHaves(joinArr(res.data.must_haves));
      setDealBreakers(joinArr(res.data.deal_breakers));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save changes.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDiscoverable = async () => {
    const ok = await patch({ discoverable: !profile.discoverable });
    if (ok) toast.success(profile.discoverable ? 'You are now hidden from recruiters.' : 'You are now discoverable.');
  };

  const handleAvailability = async (value) => {
    const ok = await patch({ availability: value });
    if (ok) toast.success('Availability updated.');
  };

  const handleTimeline = async (value) => {
    const ok = await patch({ timeline: value });
    if (ok) toast.success('Timeline updated.');
  };

  const handleRespondIntro = async (introId, accept) => {
    try {
      await manageRespondIntro(token, introId, accept);
      toast.success(accept ? 'Intro accepted — the recruiter can now reach you.' : 'Intro declined.');
      loadIntros(token);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not respond.');
    }
  };

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    const ok = await patch({
      desired_roles: desiredRoles,
      skills: splitArr(skills),
      must_haves: splitArr(mustHaves),
      deal_breakers: splitArr(dealBreakers),
    });
    if (ok) toast.success('Profile updated.');
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove yourself from the talent pool? This deletes your profile and cannot be undone.')) {
      return;
    }
    setSaving(true);
    try {
      await manageDelete(token);
      setRemoved(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove your profile.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'appearance-none block w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3.5 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm transition-colors focus:border-[#2563EB] focus:outline-none focus:ring-1 focus:ring-[#2563EB]';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="mb-1 inline-flex w-full items-center justify-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: BLUE }} />
          Manage your profile
        </div>
      </div>
      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-lg">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-8 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)] sm:px-10"
        >
          <Link to="/" className="flex justify-center">
            <img className="h-20 w-auto" src="/AutoHire.png" alt="AutoHire" />
          </Link>
          {children}
        </motion.div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin" /> Loading your profile…
        </div>
      </Shell>
    );
  }

  if (removed) {
    return (
      <Shell>
        <div className="mt-6 text-center">
          <h2 className="font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
            You've been removed
          </h2>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
            Your profile is no longer in the talent pool. You can rejoin anytime.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link
              to="/join"
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              style={{ background: BLUE }}
            >
              Rejoin the pool
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  if (error || !profile) {
    return (
      <Shell>
        <div className="mt-6 text-center">
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Can't load this profile
          </h2>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">{error}</p>
          <div className="mt-6 flex justify-center">
            <Link
              to="/join"
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              style={{ background: BLUE }}
            >
              Join the pool
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h2 className="mt-5 text-center font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
        {profile.full_name || 'Your profile'}
      </h2>
      <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
        {profile.email}
      </p>

      {/* Discoverability toggle */}
      <div className="mt-7 flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 px-4 py-3.5">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ background: '#EFF4FE', color: BLUE }}
          >
            {profile.discoverable ? <Eye size={18} /> : <EyeOff size={18} />}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {profile.discoverable ? 'Discoverable' : 'Hidden'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {profile.discoverable ? 'Recruiters can find you' : 'You are not in the pool'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleToggleDiscoverable}
          disabled={saving}
          role="switch"
          aria-checked={profile.discoverable}
          className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60"
          style={{ background: profile.discoverable ? BLUE : '#CBD5E1' }}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              profile.discoverable ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {/* Availability */}
      <div className="mt-6">
        <span className={labelClass}>Availability</span>
        <div className="mt-2 grid grid-cols-1 gap-2">
          {AVAILABILITY.map((a) => {
            const active = profile.availability === a.value;
            return (
              <button
                key={a.value}
                type="button"
                onClick={() => handleAvailability(a.value)}
                disabled={saving}
                className={`rounded-lg border px-3.5 py-2.5 text-left text-sm transition disabled:opacity-60 ${
                  active
                    ? 'border-[#2563EB] bg-blue-50 dark:bg-blue-950/30 text-gray-900 dark:text-white'
                    : 'border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
                style={active ? { boxShadow: `inset 0 0 0 1px ${BLUE}` } : undefined}
              >
                {a.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-6">
        <span className={labelClass}>Timeline</span>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {TIMELINE.map((t) => {
            const active = profile.timeline === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => handleTimeline(t.value)}
                disabled={saving}
                className={`rounded-lg border px-3.5 py-2.5 text-center text-sm transition disabled:opacity-60 ${
                  active
                    ? 'border-[#2563EB] bg-blue-50 dark:bg-blue-950/30 text-gray-900 dark:text-white'
                    : 'border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
                style={active ? { boxShadow: `inset 0 0 0 1px ${BLUE}` } : undefined}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Warm intro requests (double opt-in) */}
      {intros.filter((i) => i.status === 'pending').length > 0 && (
        <div className="mt-7 border-t border-gray-100 dark:border-gray-800 pt-5">
          <span className={labelClass}>Recruiters want to connect</span>
          <p className="mt-1 text-xs text-gray-500">You decide — accept to share your contact, or decline.</p>
          <div className="mt-3 space-y-2">
            {intros.filter((i) => i.status === 'pending').map((i) => (
              <div key={i.id} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 px-4 py-3">
                {i.job?.company && (
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{i.job.title} · {i.job.company}</p>
                )}
                {i.message && <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">"{i.message}"</p>}
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => handleRespondIntro(i.id, true)}
                    className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
                    style={{ background: BLUE }}
                  >
                    Accept intro
                  </button>
                  <button
                    onClick={() => handleRespondIntro(i.id, false)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Roles + skills */}
      <form className="mt-6 space-y-5" onSubmit={handleSaveDetails}>
        <div>
          <label htmlFor="desired_roles" className={labelClass}>Target roles</label>
          <div className="mt-1.5">
            <input
              id="desired_roles"
              type="text"
              value={desiredRoles}
              onChange={(e) => setDesiredRoles(e.target.value)}
              placeholder="e.g. Senior Backend Engineer"
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <label htmlFor="skills" className={labelClass}>Skills</label>
          <div className="mt-1.5">
            <input
              id="skills"
              type="text"
              value={skills}
              onChange={(e) => setSkills(e.target.value)}
              placeholder="Comma-separated, e.g. Node.js, PostgreSQL"
              className={inputClass}
            />
          </div>
          <p className="mt-1 text-xs text-gray-400">Separate with commas.</p>
        </div>
        <div>
          <label htmlFor="must_haves" className={labelClass}>Must-haves</label>
          <div className="mt-1.5">
            <input
              id="must_haves"
              type="text"
              value={mustHaves}
              onChange={(e) => setMustHaves(e.target.value)}
              placeholder="e.g. remote, equity, 4-day week"
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <label htmlFor="deal_breakers" className={labelClass}>Deal-breakers</label>
          <div className="mt-1.5">
            <input
              id="deal_breakers"
              type="text"
              value={dealBreakers}
              onChange={(e) => setDealBreakers(e.target.value)}
              placeholder="e.g. on-call, no remote"
              className={inputClass}
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          className="flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
          style={{ background: BLUE }}
          onMouseEnter={(e) => !saving && (e.currentTarget.style.background = '#1D4FD8')}
          onMouseLeave={(e) => (e.currentTarget.style.background = BLUE)}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </form>

      {/* Intent score readout */}
      <div className="mt-7 flex items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-5">
        <span className="text-[11px] uppercase tracking-wide text-gray-500">Your intent score</span>
        <span className="text-2xl font-bold tabular-nums" style={{ color: BLUE }}>
          {profile.intent_score}
        </span>
      </div>

      {/* Remove from pool */}
      <div className="mt-6">
        <button
          type="button"
          onClick={handleRemove}
          disabled={saving}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 dark:border-red-900/50 px-4 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 transition hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-60"
        >
          <Trash2 size={15} /> Remove me from the pool
        </button>
      </div>

      <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-gray-400">
        <ShieldCheck size={13} style={{ color: BLUE }} />
        You control your visibility — opt out anytime
      </p>
    </Shell>
  );
}

export default ManageProfile;
